export type AlternatesSearchScope = "all" | "batch";

export interface ParsedAlternatesSearch {
  scope: AlternatesSearchScope;
  /** Lowercase trimmed term used for matching. */
  term: string;
}

export interface AlternatesSearchDevice {
  id: number;
  deviceLabelSerial: string | null;
  boxSerial: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  finishedSku: string | null;
  stationName: string | null;
}

export interface AlternatesSearchBatchItem {
  batchItemId: number;
  batchId: number;
  manufacturerSalesOrderNumber: string | null;
}

export interface AlternatesSearchSkuGroup {
  sku: string;
  deviceCount: number;
  devices: AlternatesSearchDevice[];
  eligibleBatchItems: AlternatesSearchBatchItem[];
}

export interface AlternatesUrlSearchParams {
  /** `?search=` — same convention as batch details list filter. */
  search?: string;
  /** `?search_batch=` — sugar for batch-scoped filter (maps to `batch:<id>` in the field). */
  searchBatch?: string;
  /** Legacy `?batch=` deep links from production calendar (deprecated). */
  legacyBatch?: string;
}

/**
 * Resolve alternates board search text from URL query params.
 * Prefer `search=`; `search_batch=` and legacy `batch=` expand to `batch:<id>`.
 */
export function alternatesSearchFromUrlParams(params: AlternatesUrlSearchParams): string {
  const search = params.search?.trim() ?? "";
  if (search !== "") {
    return search;
  }
  const searchBatch = params.searchBatch?.trim() ?? "";
  if (searchBatch !== "") {
    return `batch:${searchBatch}`;
  }
  const legacyBatch = params.legacyBatch?.trim() ?? "";
  if (legacyBatch !== "") {
    return `batch:${legacyBatch}`;
  }
  return "";
}

/** Canonical deep link for batch-scoped alternates search. */
export function alternatesBatchSearchDeepLink(batchId: number | string): string {
  return `batch:${String(batchId).trim()}`;
}

/**
 * Parse alternates board search text.
 * Use `batch:2050` or `@batch 2050` to restrict to eligible lines on that batch id (exact match).
 */
export function parseAlternatesSearchQuery(raw: string): ParsedAlternatesSearch {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { scope: "all", term: "" };
  }

  const batchColonMatch = /^batch:\s*(.+)$/iu.exec(trimmed);
  if (batchColonMatch) {
    const term = batchColonMatch[1]!.trim().toLowerCase();
    return { scope: "batch", term };
  }

  const atBatchMatch = /^@(?:batch|batches)\s+(.+)$/iu.exec(trimmed);
  if (atBatchMatch) {
    const term = atBatchMatch[1]!.trim().toLowerCase();
    return { scope: "batch", term };
  }

  return { scope: "all", term: trimmed.toLowerCase() };
}

export function alternateDeviceMatchesSerialOrIdSearch(device: AlternatesSearchDevice, needleLower: string): boolean {
  if (needleLower === "") return false;
  const fields = [
    device.deviceLabelSerial,
    device.boxSerial,
    device.pcbaSerial,
    device.clampSerial,
    device.finishedSku,
    String(device.id),
  ];
  return fields.some((value) => value != null && String(value).trim().toLowerCase().includes(needleLower));
}

export function eligibleBatchItemMatchesSearch(batchItem: AlternatesSearchBatchItem, needleLower: string): boolean {
  if (needleLower === "") return false;
  if (String(batchItem.batchId).toLowerCase().includes(needleLower)) return true;
  const order = batchItem.manufacturerSalesOrderNumber;
  if (order != null && order.trim().toLowerCase().includes(needleLower)) return true;
  return false;
}

/** Exact batch id match for scoped batch search. */
export function eligibleBatchItemMatchesBatchScope(batchItem: AlternatesSearchBatchItem, batchTermLower: string): boolean {
  if (batchTermLower === "") return false;
  return String(batchItem.batchId).toLowerCase() === batchTermLower;
}

export function alternateGroupMatchesBatchSearch(group: AlternatesSearchSkuGroup, needleLower: string): boolean {
  if (needleLower === "") return false;
  return group.eligibleBatchItems.some((item) => eligibleBatchItemMatchesSearch(item, needleLower));
}

export function stationFilterValue(device: AlternatesSearchDevice): string {
  const name = device.stationName;
  return name == null || name.trim() === "" ? "No station" : name.trim();
}

export function filterAlternatesSkuGroups<T extends AlternatesSearchSkuGroup>(input: {
  groups: readonly T[];
  parsedSearch: ParsedAlternatesSearch;
  selectedStations: readonly string[];
}): T[] {
  const { groups, parsedSearch, selectedStations } = input;
  const selectedStationSet = new Set(selectedStations);
  const stationFilterActive = selectedStationSet.size > 0;
  const batchScopeActive = parsedSearch.scope === "batch" && parsedSearch.term !== "";
  const textFilterActive = parsedSearch.scope === "all" && parsedSearch.term !== "";
  const filtersActive = batchScopeActive || textFilterActive || stationFilterActive;

  if (!filtersActive) {
    return [...groups] as T[];
  }

  return groups
    .map((group): T | null => {
      if (batchScopeActive) {
        const eligibleBatchItems = group.eligibleBatchItems.filter((item) =>
          eligibleBatchItemMatchesBatchScope(item, parsedSearch.term),
        );
        if (eligibleBatchItems.length === 0) {
          return null;
        }
        const devices = group.devices.filter(
          (device) => !stationFilterActive || selectedStationSet.has(stationFilterValue(device)),
        );
        if (devices.length === 0) {
          return null;
        }
        return {
          ...group,
          deviceCount: devices.length,
          devices,
          eligibleBatchItems,
        };
      }

      const batchMatchesSearch = alternateGroupMatchesBatchSearch(group, parsedSearch.term);
      const devices = group.devices.filter((device) => {
        const matchesText =
          parsedSearch.term === "" ||
          alternateDeviceMatchesSerialOrIdSearch(device, parsedSearch.term) ||
          batchMatchesSearch;
        const matchesStation = !stationFilterActive || selectedStationSet.has(stationFilterValue(device));
        return matchesText && matchesStation;
      });
      if (devices.length === 0) {
        return null;
      }
      return {
        ...group,
        deviceCount: devices.length,
        devices,
      };
    })
    .filter((group): group is T => group != null);
}
